import { AutomateIcon } from '@eleveo/icons-react';
import { Tooltip } from '@zoom/components';
import { max, min } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import { XAxisOptions } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { TFunction } from 'i18next';
import { pathOr } from 'ramda';
import React, { Dispatch } from 'react';
import { v4 } from 'uuid';

import { DatePickerValueType } from '../../../components/DatePicker/DatePicker';
import { ProductionIcon } from '../../../components/ListIcons/ProductionIcon';
import { HeaderPropsType } from '../../../components/layout/Content';
import { LockType } from '../../../graphql/components';
import { CreateEditViewType } from '../../../types';
import { defaultScrollPosition, formatScheduleElementRGBAColor, ScrollPosition } from '../../../utils/chart';
import { createEndOfDateRange, convertFieldsEndOfDateRange } from '../../../utils/dateRange';
import { CHART_HOUR_INTERVAL_WIDTH, HOURS_24, MIN_5 } from '../config';
import { getButtonTestId } from '../constants';
import { SchedulingState, ChartScheduleElement, ScheduleElement, ScheduleHistory } from '../types';
import { SchedulingHeader } from './SchedulingHeader';
import { Action, addElement, updateDate, rewindSchedule } from './actions';

export interface OtherHeaderPropsType {
  action: CreateEditViewType;
  activeSchedulesIds: string[];
  endTime: number;
  handleCreateAutoScheduleJob?: () => void;
  handlePreviousStep: () => void;
  handleSubmit: () => void;
  handleToggleAddPersonsSidebar: () => void;
  handleToggleScheduleConfiguration: () => void;
  isEdit: boolean;
  isProduction: boolean;
  isScheduleConfigurationSidebarOpen: boolean;
  isView: boolean;
  lockType: LockType;
  pickedTimeLocal: number;
  pickedTimeViewed: number;
  scheduleEndsInFuture: boolean;
  scheduleHistory: ScheduleHistory;
  scheduleId?: string;
  startTime: number;
  timezone: string;
  updateProductionState: () => void;
  isAutoScheduleLimitExceeded: boolean;
  isAutoScheduleRangeOverlapping: boolean;
  existAgentWithAssignedShiftTemplate: boolean;
}

export const getHeaderProps = (
  t: TFunction,
  dispatch: Dispatch<Action>,
  subtitle: string,
  {
    action,
    activeSchedulesIds,
    endTime,
    existAgentWithAssignedShiftTemplate,
    handleCreateAutoScheduleJob,
    handlePreviousStep,
    handleSubmit,
    handleToggleAddPersonsSidebar,
    handleToggleScheduleConfiguration,
    isAutoScheduleLimitExceeded,
    isAutoScheduleRangeOverlapping,
    isEdit,
    isProduction,
    isScheduleConfigurationSidebarOpen,
    isView,
    lockType,
    pickedTimeLocal,
    pickedTimeViewed,
    scheduleEndsInFuture,
    scheduleHistory,
    scheduleId,
    startTime,
    timezone,
    updateProductionState,
  }: OtherHeaderPropsType
): HeaderPropsType => {
  const handleDateChange = (date: DatePickerValueType) =>
    date && dispatch(updateDate(zonedTimeToUtc(new Date(date), timezone).getTime()));

  const handleNextDayChange = () => handleDateChange(createEndOfDateRange(pickedTimeLocal));
  const handlePreviousDayChange = () => handleDateChange(convertFieldsEndOfDateRange(pickedTimeLocal));

  const handleRewindSchedule = (statePosition: number) => dispatch(rewindSchedule(statePosition));

  return {
    subtitle,
    actionBar: (
      <SchedulingHeader
        action={action}
        activeSchedulesIds={activeSchedulesIds}
        endTime={endTime}
        existAgentWithAssignedShiftTemplate={existAgentWithAssignedShiftTemplate}
        handleCreateAutoScheduleJob={handleCreateAutoScheduleJob}
        handleDateChange={handleDateChange}
        handleNextDay={handleNextDayChange}
        handlePreviousDay={handlePreviousDayChange}
        handlePreviousStep={handlePreviousStep}
        handleRewindSchedule={handleRewindSchedule}
        handleSubmit={handleSubmit}
        handleToggleAddPersonsSidebar={handleToggleAddPersonsSidebar}
        handleToggleScheduleConfiguration={handleToggleScheduleConfiguration}
        isAutoScheduleLimitExceeded={isAutoScheduleLimitExceeded}
        isAutoScheduleRangeOverlapping={isAutoScheduleRangeOverlapping}
        isEdit={isEdit}
        isProduction={isProduction}
        isScheduleConfigurationSidebarOpen={isScheduleConfigurationSidebarOpen}
        isView={isView}
        lockType={lockType}
        pickedTimeLocal={pickedTimeLocal}
        pickedTimeViewed={pickedTimeViewed}
        scheduleEndsInFuture={scheduleEndsInFuture}
        scheduleHistory={scheduleHistory}
        scheduleId={scheduleId}
        startTime={startTime}
        t={t}
        timezone={timezone}
        updateProductionState={updateProductionState}
      />
    ),
    icon: (
      <>
        <ProductionIcon
          /**
           * isProduction needs to be a negated value as we want to show current status, not to revert it
           * (the naming convention came from old implementation, however it seems to be a kind of misleading)
           */
          isProduction={!isProduction}
          tooltipMessageKey={isProduction ? 'layout.states.inProduction' : 'layout.states.inDraft'}
          color={isProduction ? 'green' : 'orange'}
          size={26}
        />
        {lockType === LockType.Write && (
          <Tooltip placement="bottom" message={t(`schedule.autoSchedule.inProgress`)}>
            <AutomateIcon className="text-2xl text-chart-300 ml-2 mr-1" />
          </Tooltip>
        )}
      </>
    ),
    testId: getButtonTestId(action, 2, 'title'),
    title: t(`schedule.${action}`),
    titleSlug: `${action}-schedule`,
  };
};

export const getCoords = (elem: Element) => {
  // crossbrowser version
  const box = elem.getBoundingClientRect();

  const { body } = document;
  const docEl = document.documentElement;

  const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
  const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;

  const clientTop = docEl.clientTop || body.clientTop || 0;
  const clientLeft = docEl.clientLeft || body.clientLeft || 0;

  const top = box.top + scrollTop - clientTop;
  const left = box.left + scrollLeft - clientLeft;

  return { top: Math.round(top), left: Math.round(left) };
};

export const handleMouseUp = (
  element: ScheduleElement | null,
  state: SchedulingState,
  dispatch: Dispatch<Action>,
  ganttChartRef: React.RefObject<HighchartsReact>,
  e: Event
) => {
  if (!element || !ganttChartRef.current) {
    return;
  }

  const { agents, chartState, visibleSupervisedAgentIds } = state;
  const normalized = ganttChartRef.current.chart.pointer.normalize(e as PointerEvent);
  const coordinates = ganttChartRef.current.chart.pointer.getCoordinates(normalized);
  const chart = document.getElementsByClassName('highcharts-root')[0];
  const dragArea = document.getElementsByClassName('drag-drop');
  const droppedElement = dragArea[0].querySelector('.draggableItemParent.dragged');

  if (!droppedElement) {
    return;
  }

  const chartStartTime = (chartState.xAxis as XAxisOptions[])[0].min as number;

  // X coordinate by left border of the dropped element
  const droppedXCoordinate = ganttChartRef.current.chart.xAxis[0].toValue(
    getCoords(droppedElement).left - getCoords(chart).left
  );

  const elementDuration = element.defaultDuration;
  const isWholeDayElement = elementDuration >= HOURS_24;

  const countOfIntervals = Math.round((droppedXCoordinate - chartStartTime) / MIN_5);
  let newElementStartTime = chartStartTime + countOfIntervals * MIN_5;
  let newElementEndTime = newElementStartTime + elementDuration;
  const elementName = element.name;

  const startBoundary = max([
    (chartState.xAxis as XAxisOptions[])[0].min as number,
    chartState.plotOptions!.series!.dragDrop!.dragMinX as number,
  ]).getTime();

  const endBoundary = min([
    (chartState.xAxis as XAxisOptions[])[0].max as number,
    chartState.plotOptions!.series!.dragDrop!.dragMaxX as number,
  ]).getTime();

  const droppedElementDurationInMs = (element.defaultDropElementWidth / CHART_HOUR_INTERVAL_WIDTH) * 60 * 60 * 1000;

  if (
    (newElementStartTime < startBoundary && newElementEndTime <= startBoundary) ||
    (newElementStartTime >= endBoundary && newElementEndTime > endBoundary) ||
    // whole day element fully placed into out of schedule range area
    (isWholeDayElement &&
      newElementStartTime < startBoundary &&
      newElementStartTime + droppedElementDurationInMs < startBoundary)
  ) {
    // do not add elements if they don't fit into boundaries at least partially
    return;
  }

  // position precision, for cases when element should be aligned at 00:00
  if (newElementStartTime < startBoundary) {
    newElementStartTime = startBoundary;
    newElementEndTime = newElementStartTime + elementDuration;
  }

  /**
   * position correction, for cases when element is placed at the end of the scheduled day
   * having some of its part outside of the chart
   */
  if (newElementEndTime > endBoundary && !isWholeDayElement) {
    newElementEndTime = endBoundary;
    newElementStartTime = newElementEndTime - elementDuration;
  }

  if (isWholeDayElement) {
    newElementEndTime = createEndOfDateRange(chartStartTime).getTime();
  }

  const elementYPosition = Math.round(coordinates.yAxis[0].value);
  const agent = pathOr(null, [visibleSupervisedAgentIds[elementYPosition]], agents);

  /**
   * Skip adding element when:
   *  - element dropped out of the plot area or
   *  - agent is not active or
   *  - agent is not assigned to any of queues used within schedule
   */
  if (!agent || !agent.active || agent.isNotAssigned) return;

  const id = v4();

  const newElement: ChartScheduleElement = {
    start: newElementStartTime,
    end: newElementEndTime,
    scheduleElementName: elementName,
    scheduleElementNameSize: element.nameSize,
    scheduleElementOriginalName: element.originalName,
    y: elementYPosition,
    key: id,
    id,
    color: formatScheduleElementRGBAColor(element.color),
    scheduleElementId: element.id,
    agentId: agent.id,
    paid: element.paid,
    paidTimeOff: element.paidTimeOff,
    pinnable: element.pinnable,
    pinned: false,
    productive: element.productive,
    shift: element.shift,
    selected: false,
    wholeDayElement: isWholeDayElement,
    dayIndexes: [],
  };

  dispatch(addElement({ newElement }));
};

const SCROLL_POSITION_STORAGE_KEY = 'scrollPosition';

export const setScrollPositionInStorage = (value: ScrollPosition = defaultScrollPosition) => {
  sessionStorage.setItem(SCROLL_POSITION_STORAGE_KEY, JSON.stringify(value));
};

export const getScrollPositionFromStorage = () => {
  const scrollPosition = sessionStorage.getItem(SCROLL_POSITION_STORAGE_KEY);
  return scrollPosition ? (JSON.parse(scrollPosition) as ScrollPosition) : defaultScrollPosition;
};

export const unselectBrowserSelection = () => {
  // representing the range of text selected by the user
  const browsersSelection = document.getSelection();
  if (browsersSelection) {
    browsersSelection.removeAllRanges();
  }
};
